import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthContext';
import { createAccount } from '../../lib/api/accounts';
import { Button } from '../ui';
import { accountIdFromName } from '../../../../shared/types/accounts';

/**
 * Active-workspace switcher shown in the header.
 *
 * Always renders as a dropdown (even when the user has a single
 * workspace), so the "Create new workspace" action is always
 * discoverable. The dropdown lists every workspace the user belongs to
 * plus a footer button for creating an additional one.
 *
 * Switching persists via AuthContext.setCurrentAccount (→ localStorage)
 * and invalidates cached queries so projects/repos/health re-fetch
 * under the new scope.
 */
export const AccountSelector: React.FC = () => {
  const { me, currentAccount, setCurrentAccount, refreshMe } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const accounts = me?.accounts ?? [];

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!me) return null;

  const choose = (id: string) => {
    if (currentAccount && id !== currentAccount.id) {
      setCurrentAccount(id);
      qc.invalidateQueries();
      // Carry the workspace in the URL (?ws=) so a copied link opens the same
      // workspace for any member — see WorkspaceParamSync.
      navigate(`/projects?ws=${encodeURIComponent(id)}`);
    }
    setOpen(false);
  };

  const onCreateClicked = () => {
    setOpen(false);
    setShowCreate(true);
  };

  return (
    <>
      <div className="relative" ref={ref} data-testid="account-selector">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                     text-slate-700 border border-slate-200 bg-white hover:bg-slate-50
                     transition-colors max-w-[220px]"
        >
          <span aria-hidden className="text-slate-400">⊞</span>
          <span className="truncate">{currentAccount?.name ?? 'No workspace'}</span>
          <span aria-hidden className="text-slate-400">▾</span>
        </button>

        {open && (
          <div
            role="listbox"
            aria-label="Switch account"
            className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40"
          >
            <div className="px-3 py-1.5 text-2xs uppercase tracking-wide text-slate-400 font-semibold">
              Your workspaces
            </div>
            {accounts.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-500">No workspaces yet.</div>
            )}
            {accounts.map((a) => {
              const active = a.id === currentAccount?.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => choose(a.id)}
                  data-testid={`account-option-${a.id}`}
                  className={
                    'w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors ' +
                    (active ? 'bg-primary-50' : 'hover:bg-slate-50')
                  }
                >
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-slate-900">{a.name}</span>
                    <span className="block text-2xs text-slate-400 font-mono truncate">
                      {a.id} · {a.role}
                    </span>
                  </span>
                  {active && <span aria-hidden className="text-primary-600">✓</span>}
                </button>
              );
            })}
            <div className="border-t border-slate-100 my-1" />
            <button
              type="button"
              onClick={onCreateClicked}
              data-testid="account-create-trigger"
              className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm text-primary-600 hover:bg-slate-50 transition-colors"
            >
              <span aria-hidden>＋</span>
              Create new workspace
            </button>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateWorkspaceModal
          onClose={() => setShowCreate(false)}
          onCreated={async (id) => {
            // Pull the fresh /auth/me so the new account is in the list.
            await refreshMe();
            setCurrentAccount(id);
            qc.invalidateQueries();
            setShowCreate(false);
            navigate('/projects');
          }}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Create-workspace modal
// ---------------------------------------------------------------------------

const CreateWorkspaceModal: React.FC<{
  onClose: () => void;
  onCreated: (id: string) => void | Promise<void>;
}> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const trimmedName = name.trim();
  const derivedId = idTouched ? id.trim() : (trimmedName ? accountIdFromName(trimmedName) ?? '' : '');

  // Keep the id field synced to the name until the user edits it directly.
  useEffect(() => {
    if (!idTouched) setId(trimmedName ? accountIdFromName(trimmedName) ?? '' : '');
  }, [trimmedName, idTouched]);

  const create = useMutation({
    mutationFn: async () => {
      const res = await createAccount({ name: trimmedName, id: derivedId || undefined });
      return res;
    },
    onSuccess: (acc) => onCreated(acc.id),
  });

  const nameError = submitted && trimmedName === '' ? 'Name is required' : null;
  const idError =
    submitted && derivedId === '' ? 'Could not derive an id from this name — type one' : null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (trimmedName === '' || derivedId === '') return;
    create.mutate();
  };

  // Portal to <body>: this modal is rendered inside the sticky header, whose
  // `backdrop-blur` (backdrop-filter) makes it the containing block for
  // `position: fixed` descendants — the overlay was confined to the ~56px
  // header strip, so "Create new workspace" looked like it did nothing.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create workspace"
      className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-workspace-modal"
      >
        <div className="space-y-1.5">
          <div className="font-medium text-slate-900">Create a new workspace</div>
          <p className="text-sm text-slate-600">
            You become the owner. Once it's created you can invite teammates — including users
            on different email addresses — from the Members page.
          </p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="ws-name" className="block text-xs text-slate-600">Workspace name</label>
          <input
            id="ws-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Studios"
            autoFocus
            maxLength={120}
            data-testid="create-workspace-name"
            className="block w-full border border-slate-300 rounded-md text-sm px-2.5 py-1.5"
          />
          {nameError && <p role="alert" className="text-xs text-danger-600">{nameError}</p>}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="ws-id" className="block text-xs text-slate-600">
            Slug <span className="text-slate-400">(URL + DDB key)</span>
          </label>
          <input
            id="ws-id"
            type="text"
            value={id}
            onChange={(e) => { setId(e.target.value.toLowerCase()); setIdTouched(true); }}
            placeholder="acme-studios"
            maxLength={32}
            data-testid="create-workspace-id"
            className="block w-full border border-slate-300 rounded-md text-sm px-2.5 py-1.5 font-mono"
          />
          {idError && <p role="alert" className="text-xs text-danger-600">{idError}</p>}
        </div>

        {create.error && (
          <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
            {(create.error as Error).message}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" loading={create.isPending}>
            Create workspace
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default AccountSelector;
